import { FC, useCallback, useMemo } from 'react';

import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EUIButtonBaseOpacity } from '@aquariuxfintech/aqx-trader-react-ui/button';
import { ESpacing } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS_WITHOUT_CRM } from '@aqx-core/common/api/auth';
import { EWebtraderResetPasswordType } from '@aqx-core/common/api/config';
import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { useAuthRoutes } from '@aqx-core/common/hooks/auth/use-auth-routes';
import { useAuthLoading } from '@aqx-core/common/services/auth';
import { rgba } from '@aqx-core/common/theme';
import { openUrl } from '@aqx-member/web/utils/url/open-url';

interface ILoginForgotPasswordProps {
  selectedTab: ELoginAs | undefined;
}

const LoginForgotPassword: FC<ILoginForgotPasswordProps> = ({ selectedTab }) => {
  const { t } = useTranslation();
  const authLoginLoading = useAuthLoading();
  const navigate = useNavigate();
  const { hasCrmForgotPasswordButton, hasLiveForgotPasswordButton, hasDemoResetPassword, companyProfile } =
    useAuthRoutes();

  const { webtraderResetPasswordType, crmForgotPasswordUrl, webtraderResetPasswordUrl, crmForgotPasswordType } =
    companyProfile || {};

  const isNonCrmIntegrationResetPasswordType = webtraderResetPasswordType === EWebtraderResetPasswordType.INTEGRATION;

  const isCrmIntegrationResetPasswordType = crmForgotPasswordType === EWebtraderResetPasswordType.INTEGRATION;

  const { text: forgotText, onClick } = useMemo(() => {
    const FORGOT_PASSWORD = t('Forgot Password');
    const RESET_PASSWORD = t('Reset Password');

    // CRM
    if (selectedTab === ELoginAs.LIVE && hasCrmForgotPasswordButton) {
      const onClick = () =>
        isCrmIntegrationResetPasswordType
          ? navigate(AUTHENTICATION_ROUTES.FORGOT_PASSWORD_CRM)
          : openUrl(crmForgotPasswordUrl, '_blank');

      return { text: FORGOT_PASSWORD, onClick };
    }
    if (selectedTab && LIVE_LOGINS_WITHOUT_CRM.includes(selectedTab) && hasLiveForgotPasswordButton) {
      const onClick = () =>
        isNonCrmIntegrationResetPasswordType
          ? navigate(AUTHENTICATION_ROUTES.FORGOT_PASSWORD_LIVE)
          : openUrl(webtraderResetPasswordUrl, '_blank');

      return { text: FORGOT_PASSWORD, onClick };
    }
    if (selectedTab && DEMO_LOGINS.includes(selectedTab) && hasDemoResetPassword) {
      const onClick = () => navigate(AUTHENTICATION_ROUTES.RESET_PASSWORD);
      return { text: RESET_PASSWORD, onClick };
    }
    return { text: '', onClick: () => {} };
  }, [
    crmForgotPasswordUrl,
    hasCrmForgotPasswordButton,
    hasDemoResetPassword,
    hasLiveForgotPasswordButton,
    isCrmIntegrationResetPasswordType,
    isNonCrmIntegrationResetPasswordType,
    navigate,
    selectedTab,
    t,
    webtraderResetPasswordUrl
  ]);

  const onClickHandler = useCallback(() => {
    if (authLoginLoading) {
      return;
    }
    onClick();
  }, [authLoginLoading, onClick]);

  if (!forgotText) {
    return null;
  }

  return (
    <StyledContainer>
      <StyledText as="span" onClick={onClickHandler} $disabled={authLoginLoading}>
        {forgotText}
      </StyledText>
    </StyledContainer>
  );
};

export default LoginForgotPassword;

const StyledContainer = styled.div`
  margin-top: ${ESpacing.REGULAR}px;
  align-self: end;
`;

const StyledText = styled(UIText)<{ $disabled?: boolean }>`
  margin-top: ${ESpacing.XXS}px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ theme, $disabled }) =>
    $disabled ? rgba(theme.themeColors.primary, EUIButtonBaseOpacity.DISABLED) : theme.themeColors.primary};
`;
